import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Calculator, Users, Briefcase, Sparkles, Building2, FileText, ArrowLeft, RefreshCw, Zap, CheckCircle2 } from 'lucide-react';
import PageAnimator from '../../components/ui/PageAnimator';
import { Button } from '../../components/ui/button';

// Composants du module Impôts
import FoyerFiscalForm from '../../features/impots/components/FoyerFiscalForm';
import CategorielRevenus from '../../features/impots/components/CategorielRevenus';
import DeductionsNiches from '../../features/impots/components/DeductionsNiches';
import IfiCalculator from '../../features/impots/components/IfiCalculator';
import FiscalSummaryWaterfall from '../../features/impots/components/FiscalSummaryWaterfall';
import AiTaxAdvisor from '../../features/impots/components/AiTaxAdvisor';

import {
  calculateTaxProfile
} from '../../features/impots/utils/taxCalculations';

export default function Impots() {
  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("foyer"); // foyer | revenus | deductions | ifi | bilan

  // Données complètes du profil fiscal
  const [fiscalData, setFiscalData] = useState({
    statut: 'celibataire',
    enfants: 0,
    enfantsGardeAlternee: 0,
    parentIsole: false,
    personneInvalide: false,

    // Traitements & Salaires
    salaireDeclarant1: 65000,
    salaireDeclarant2: 0,
    typeFrais: 'forfait',
    fraisReelsDeclarant1: 0,
    fraisReelsDeclarant2: 0,

    // Revenus Fonciers
    regimeFoncier: 'reel',
    loyersBruts: 18000,
    chargesDeductiblesFoncier: 3500,
    interetsEmpruntFoncier: 4200,
    travauxFoncier: 1500,

    // RCM & Dividendes
    dividendesBruts: 12000,
    optionDividendes: 'pfu',

    // Autres revenus
    bicBncPro: 0,
    pensionsRetraite: 0,

    // Épargne Retraite (PER)
    versementPer: 8000,

    // Réductions et Crédits
    enfantsCollege: 0,
    enfantsLycee: 1,
    enfantsSuperieur: 0,
    emploiDomicile: 3000,
    donsColuche: 500,
    donsGeneral: 400,
    investissementsDefisc: 0,

    // IFI
    ifiResidencePrincipale: 750000,
    ifiLocatifSecondaire: 400000,
    ifiScpi: 80000,
    ifiDettes: 250000
  });

  // Si des données sont transmises via la redirection (ex: DetailPage)
  useEffect(() => {
    if (location.state?.fiscalData) {
      const fd = location.state.fiscalData;
      setFiscalData(prev => ({
        ...prev,
        statut: fd.statut || prev.statut,
        enfants: Number(fd.enfants) || 0,
        salaireDeclarant1: Number(fd.revenus) || prev.salaireDeclarant1,
        versementPer: Number(fd.deductions) || prev.versementPer
      }));
    } else if (location.state?.clientType === "societe") {
      setFiscalData(prev => ({
        ...prev,
        salaireDeclarant1: Number(location.state.remunerationDirigeant) || 0,
        dividendesBruts: Number(location.state.dividendes) || 0,
        loyersBruts: 0,
        chargesDeductiblesFoncier: 0,
        interetsEmpruntFoncier: 0,
        travauxFoncier: 0,
        versementPer: 0,
        emploiDomicile: 0,
        donsColuche: 0,
        donsGeneral: 0,
        ifiResidencePrincipale: 0,
        ifiLocatifSecondaire: 0,
        ifiScpi: 0,
        ifiDettes: 0
      }));
    }
  }, [location.state]);

  // Calcul du profil fiscal en temps réel
  const calculations = calculateTaxProfile(fiscalData);
  const parts = calculations.parts;

  const tabs = [
    { id: "foyer", label: "Foyer & Parts Fiscales", icon: Users },
    { id: "revenus", label: "Revenus Catégoriels (IR)", icon: Briefcase },
    { id: "deductions", label: "Déductions & PER", icon: Sparkles },
    { id: "ifi", label: "Simulateur IFI", icon: Building2 },
    { id: "bilan", label: "Synthèse & Audit IA", icon: FileText },
  ];

  const handleResetDefaults = () => {
    setFiscalData({
      statut: 'celibataire',
      enfants: 0,
      enfantsGardeAlternee: 0,
      parentIsole: false,
      personneInvalide: false,
      salaireDeclarant1: 50000,
      salaireDeclarant2: 0,
      typeFrais: 'forfait',
      fraisReelsDeclarant1: 0,
      fraisReelsDeclarant2: 0,
      regimeFoncier: 'reel',
      loyersBruts: 0,
      chargesDeductiblesFoncier: 0,
      interetsEmpruntFoncier: 0,
      travauxFoncier: 0,
      dividendesBruts: 0,
      optionDividendes: 'pfu',
      bicBncPro: 0,
      pensionsRetraite: 0,
      versementPer: 0,
      enfantsCollege: 0,
      enfantsLycee: 0,
      enfantsSuperieur: 0,
      emploiDomicile: 0,
      donsColuche: 0,
      donsGeneral: 0,
      investissementsDefisc: 0,
      ifiResidencePrincipale: 0,
      ifiLocatifSecondaire: 0,
      ifiScpi: 0,
      ifiDettes: 0
    });
  };

  return (
    <div className="p-4 sm:p-8 bg-workspace min-h-screen pb-24">
      <PageAnimator>
        {/* Header Institutionnel */}
        <div
          className="
            relative z-20
            flex shrink-0 flex-col
            gap-6
            overflow-visible
            workspace-card
            bg-cover bg-center bg-no-repeat
            p-5 sm:p-8
            mb-8
            xl:flex-row xl:items-center xl:justify-between
            rounded-3xl
            border border-workspace
            shadow-hp-md
          "
          style={{ backgroundImage: "url('/assets/backgrounds/lighthouse_bg.png')" }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0 rounded-3xl bg-overlay"
          />

          <div className="relative z-10 flex min-w-0 shrink-0 flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-5">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-secondary text-white shadow-hp-md border border-white/20">
              <Calculator className="h-7 w-7" />
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                  Simulateur d'Ingénierie Fiscale (IR & IFI)
                </h1>
                {location.state?.clientName && (
                  <span className="hidden sm:inline-flex items-center gap-1 px-3 py-0.5 rounded-full bg-white/10 text-white text-xs font-semibold backdrop-blur-md border border-white/20">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {location.state.clientName}
                  </span>
                )}
              </div>
              <p className="mt-1.5 text-sm text-white/80 max-w-2xl">
                Optimisation multi-catégorielle : barème progressif 2024, arbitrage Flat Tax vs barème dividendes, déductions PER et audit IA.
              </p>
            </div>
          </div>

          <div className="relative z-10 flex items-center gap-3">
            {location.state?.clientId && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(location.state?.clientType === "societe" ? `/prospects/societes/${location.state.clientId}` : `/prospects/familles/${location.state.clientId}`)}
                className="gap-1.5 text-xs bg-white/10 hover:bg-white/20 border-white/20 text-white backdrop-blur-md"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Retour au Dossier
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetDefaults}
              className="gap-1.5 text-xs bg-white/10 hover:bg-white/20 border-white/20 text-white backdrop-blur-md"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Réinitialiser
            </Button>
          </div>
        </div>

        {/* Bannière contextuelle de préremplissage */}
        {location.state?.clientName && (
          <div className="mb-6 flex items-center justify-between p-4 rounded-2xl bg-[var(--hp-info-50)] dark:bg-[var(--hp-info-900)]/20 text-[var(--hp-info-700)] dark:text-[var(--hp-info-300)] border border-[var(--hp-info-100)] dark:border-[var(--hp-info-800)] text-sm shadow-hp-sm">
            <div className="flex items-center gap-2.5">
              <Zap className="w-4 h-4 text-secondary shrink-0" />
              <span>
                {location.state?.clientType === "societe" ? (
                  <>
                    Flux du dirigeant importés depuis la société <strong>{location.state.clientName}</strong> (Rémunération : {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(location.state.remunerationDirigeant || 0)}, Dividendes : {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(location.state.dividendes || 0)}). Vous pouvez ajuster les paramètres fiscaux du foyer.
                  </>
                ) : (
                  <>
                    Données fiscales importées automatiquement depuis la fiche de <strong>{location.state.clientName}</strong>. Vous pouvez modifier les valeurs pour tester différents scénarios d'optimisation.
                  </>
                )}
              </span>
            </div>
          </div>
        )}

        {/* Barre d'Onglets Segmentés */}
        <div className="flex border-b border-workspace mb-6 overflow-x-auto gap-2 scrollbar-none">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold whitespace-nowrap transition-all border-b-2 ${
                  isActive
                    ? "border-secondary text-primary"
                    : "border-transparent text-subtle hover:text-primary hover:border-[var(--hp-color-border-strong)]"
                }`}
              >
                <Icon className={`w-4 h-4 ${isActive ? "text-secondary" : "text-subtle"}`} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Contenu par onglet */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="space-y-6"
          >
            {activeTab === "foyer" && (
              <FoyerFiscalForm
                data={fiscalData}
                onChange={setFiscalData}
                parts={parts}
              />
            )}

            {activeTab === "revenus" && (
              <CategorielRevenus
                data={fiscalData}
                onChange={setFiscalData}
                calculations={calculations}
              />
            )}

            {activeTab === "deductions" && (
              <DeductionsNiches
                data={fiscalData}
                onChange={setFiscalData}
                calculations={calculations}
              />
            )}

            {activeTab === "ifi" && (
              <IfiCalculator
                data={fiscalData}
                onChange={setFiscalData}
              />
            )}

            {activeTab === "bilan" && (
              <>
                <FiscalSummaryWaterfall calculations={calculations} />
                <AiTaxAdvisor
                  fiscalData={fiscalData}
                  calculations={calculations}
                />
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </PageAnimator>
    </div>
  );
}
